import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import StartPage from './pages/StartPage';
import AnalysisPage from './pages/AnalysisPage';
import ChatAnalysisPage from './pages/ChatAnalysisPage';
import AccountPage from './pages/AccountPage';
import ApplicationAnalyticsPage from './pages/ApplicationAnalyticsPage';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import SdkAuthPage from './pages/SdkAuthPage';
import ConfirmEmailPage from './pages/ConfirmEmailPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import Imprint from './pages/Imprint';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import Aup from './pages/Aup';
import Dpa from './pages/Dpa';
import NotFoundPage from './pages/NotFoundPage';
import ConsentBanner from './components/ConsentBanner';
import { ToastProvider } from './context/ToastContext';
import { ThemeProvider } from './context/ThemeContext';
import { initAnalytics, trackPageView, trackEvent } from './analytics/ga';
import './styles/global.css';

// Force a fresh mount when the :id segment changes so per-analysis state
// (analysisId, messages, etc.) resets when navigating between analyses or
// from an existing analysis to /new.
const KeyedChatAnalysisPage = () => {
  const { id } = useParams();
  return <ChatAnalysisPage key={id} />;
};

// GA4 on public pages only (everything outside /app), gated by Consent Mode v2
// with all signals defaulted to denied. Page views are sent manually so GA's
// automatic history tracking never follows a logged-in user into /app.
const AnalyticsGate = () => {
  const { pathname } = useLocation();
  const isPublic = !pathname.startsWith('/app');

  useEffect(() => {
    document.documentElement.classList.toggle('app-zoomed', !isPublic);
  }, [isPublic]);

  useEffect(() => {
    if (!isPublic) return;
    initAnalytics();
    trackPageView(pathname);
  }, [pathname, isPublic]);

  // The landing CTAs live in generated markup (dangerouslySetInnerHTML), so
  // clicks are captured by delegation instead of per-element handlers.
  useEffect(() => {
    if (!isPublic) return undefined;
    const onClick = (e) => {
      if (!(e.target instanceof Element)) return;
      if (!e.target.closest('.qombra-landing')) return;
      const el = e.target.closest('a, button');
      if (!el) return;
      // Semantic markers already present in the markup, most specific first.
      const flag = ['tab', 'route', 'login', 'talk', 'demo', 'access']
        .find((k) => el.dataset[k] !== undefined);
      trackEvent('cta_click', {
        link_text: (el.textContent || '').trim().slice(0, 80),
        link_url: el.getAttribute('href') || undefined,
        element: el.tagName.toLowerCase(),
        flag: flag ? el.dataset[flag] || flag : undefined,
      });
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [isPublic]);

  return isPublic ? <ConsentBanner /> : null;
};

function App() {
  return (
    <Router>
      <ThemeProvider>
        <ToastProvider>
        <div className="App">
          <Routes>
            <Route path="/" element={<LandingPage />} />
            {/* Deep links to the two product sections. Same page, mounted at a
                clean path instead of /#qbrain, so the URL survives being pasted
                into a deck or a mail client that mangles fragments — LandingPage
                reads the pathname and scrolls to the matching section on mount. */}
            <Route path="/qbrain" element={<LandingPage />} />
            <Route path="/qagent" element={<LandingPage />} />
            <Route path="/qagent-benchmark" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            {/* Consent screen for the qombra Python SDK browser login (PKCE). */}
            <Route path="/sdk-auth" element={<SdkAuthPage />} />
            {/* Self-serve sign-up is UNLISTED, not closed: registration lives at
                /register, which nothing on the landing or login page links to —
                it is handed out directly. The old public /signup URL keeps
                redirecting to /login on purpose, so it does not become a signpost
                to the unlisted path (and because it has been out in the world and
                an unmatched route in this router renders a blank page). */}
            <Route path="/signup" element={<Navigate to="/login" replace />} />
            <Route path="/register" element={<SignupPage />} />
            <Route path="/confirm-email/:token" element={<ConfirmEmailPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
            {/* Retired — the frontier landing now makes this page's argument.
                Kept as a redirect rather than deleted outright: the URL went out
                in outreach, and an unmatched route in this router renders nothing
                at all, so a stale link would land on a blank white page. */}
            <Route path="/for-data-teams" element={<Navigate to="/" replace />} />
            <Route path="/imprint" element={<Imprint />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/aup" element={<Aup />} />
            <Route path="/dpa" element={<Dpa />} />
            <Route path="/app" element={<StartPage />} />
            {/*
              Cutover: chat-first ChatAnalysisPage now owns /app/analysis/:id.
              The legacy multi-step wizard is parked at /app/analysis-legacy/:id
              for one release of dual-running before we delete it.
            */}
            <Route path="/app/analysis/:id" element={<KeyedChatAnalysisPage />} />
            <Route path="/app/analysis-legacy/:id" element={<AnalysisPage />} />
            <Route path="/app/account" element={<AccountPage />} />
            {/* Staff-only customer usage dashboard; the API behind it enforces is_staff. */}
            <Route path="/app/application-analytics" element={<ApplicationAnalyticsPage />} />
            {/* CloudFront rewrites every extensionless non-/blog/ path to this
                SPA shell (HTTP 200), so unknown routes land here. Without a
                catch-all that meant a blank white page; now it is a visible 404.
                (/blog/ is static, outside this router; a mistyped blog URL gets
                S3's own 403 rather than reaching the SPA.) */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          <AnalyticsGate />
        </div>
        </ToastProvider>
      </ThemeProvider>
    </Router>
  );
}

export default App;
